import { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Stack, Link as MuiLink, Alert, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { sendOtp, verifyOtp, googleLogin } from '../services/authService';
import OTPInput from '../components/auth/OTPInput';
import { trackSignupStarted, trackSignupCompleted } from '../utils/analytics';

// Helper function to detect input type (email or phone)
const detectInputType = (input: string): 'email' | 'phone' | 'unknown' => {
  const trimmed = input.trim();
  
  // Check for email pattern (contains @ and .)
  if (trimmed.includes('@')) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(trimmed) ? 'email' : 'unknown';
  }
  
  // Check for phone pattern (contains only digits, +, -, (, ), spaces)
  const phoneRegex = /^[\d\s\-+()]+$/;
  if (phoneRegex.test(trimmed)) {
    // Remove non-digits to count
    const digitsOnly = trimmed.replace(/\D/g, '');
    // Phone should have 10-15 digits
    if (digitsOnly.length >= 10 && digitsOnly.length <= 15) {
      return 'phone';
    }
  }
  
  return 'unknown';
};

const PageContainer = styled(Box)(() => ({
  minHeight: '100vh',
  display: 'grid',
  gridTemplateColumns: '1fr',
  '@media (min-width: 1024px)': {
    gridTemplateColumns: '1fr 1fr'
  }
}));

const LeftPanel = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2.5rem 1rem',
  backgroundColor: 'white',
  '@media (min-width: 640px)': {
    padding: '4rem 1.5rem'
  },
  '@media (min-width: 1024px)': {
    padding: '6rem 2rem'
  }
}));

const RightPanel = styled(Box)(() => ({
  display: 'none',
  '@media (min-width: 1024px)': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4rem 2rem',
    backgroundColor: '#F9FAFB'
  }
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    '& input': {
      padding: '16px'
    },
    '& fieldset': {
      borderColor: '#E5E7EB'
    },
    '&:hover fieldset': {
      borderColor: '#D1D5DB'
    },
    '&.Mui-focused': {
      backgroundColor: 'white',
      '& fieldset': {
        borderColor: '#4F46E5',
        borderWidth: '1px'
      }
    }
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#9CA3AF',
    opacity: 1
  }
}));

const PrimaryButton = styled(Button)(() => ({
  width: '100%',
  padding: '16px',
  backgroundColor: '#4F46E5',
  color: 'white',
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none',
  borderRadius: '8px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#4338CA',
    boxShadow: 'none'
  },
  '&:disabled': {
    backgroundColor: '#D1D5DB',
    color: '#9CA3AF'
  }
}));

const SocialButton = styled(Button)(() => ({
  position: 'relative',
  width: '100%',
  padding: '16px',
  backgroundColor: 'white',
  border: '2px solid #E5E7EB',
  borderRadius: '8px',
  color: '#374151',
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none',
  justifyContent: 'center',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#F9FAFB',
    color: '#111827',
    boxShadow: 'none'
  }
}));

const SignUp = () => {
  const navigate = useNavigate();

  // Contact info - single input for email or phone
  const [emailOrPhone, setEmailOrPhone] = useState('');

  // OTP flow
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(''));
  const [resendTimer, setResendTimer] = useState(0);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [
    {
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&auto=format&fit=crop',
      title: 'Start managing reviews today',
      description: 'Join thousands of businesses already using our platform to build their online reputation and grow their customer base.'
    },
    {
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&auto=format&fit=crop',
      title: 'Simple and powerful tools',
      description: 'Everything you need to collect, manage, and showcase customer reviews. No technical expertise required.'
    },
    {
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop',
      title: 'Real-time insights',
      description: 'Track your reputation score, analyze trends, and make data-driven decisions to improve customer satisfaction.'
    }
  ];

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  // Countdown timer for OTP resend
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleGetOTP = async () => {
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      // Validate input
      if (!emailOrPhone) {
        setError('Please enter your email address or phone number');
        setIsLoading(false);
        return;
      }

      // Auto-detect input type
      const inputType = detectInputType(emailOrPhone);
      
      if (inputType === 'unknown') {
        setError('Please enter a valid email address or phone number');
        setIsLoading(false);
        return;
      }

      // Validate country code for phone numbers
      if (inputType === 'phone') {
        const trimmedPhone = emailOrPhone.trim();
        if (!trimmedPhone.startsWith('+')) {
          setError('Phone number must include country code (e.g., +1 for US, +91 for India)');
          setIsLoading(false);
          return;
        }
        
        // Validate that country code has at least 1 digit after +
        const digitsAfterPlus = trimmedPhone.substring(1).replace(/\D/g, '');
        if (digitsAfterPlus.length < 10) {
          setError('Please enter a complete phone number with country code (e.g., +1234567890)');
          setIsLoading(false);
          return;
        }
      }

      console.log(`Sending OTP to ${inputType}:`, emailOrPhone);

      // Call the API to send OTP (sendOtp accepts 'email' or 'phone')
      await sendOtp(emailOrPhone, inputType);

      // Track signup started
      trackSignupStarted(inputType);

      setSuccess(`OTP sent successfully to your ${inputType === 'email' ? 'email' : 'phone'}!`);
      setOtpSent(true);
      setResendTimer(60); // 60 second countdown

    } catch (err) {
      console.error('Send OTP error:', err);
      setError(err instanceof Error ? err.message : 'Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = () => {
    setOtp(new Array(6).fill(''));
    handleGetOTP();
  };

  const handleVerifyOTP = async () => {
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      const otpCode = otp.join('');

      // Validate OTP
      if (otpCode.length !== 6) {
        setError('Please enter the complete 6-digit OTP');
        setIsLoading(false);
        return;
      }

      // Auto-detect input type (should be the same as when OTP was sent)
      const inputType = detectInputType(emailOrPhone);

      console.log('Verifying OTP:', { contact: emailOrPhone, otpCode, method: inputType });

      // Call the API to verify OTP
      const response = await verifyOtp(emailOrPhone, otpCode, inputType);

      console.log('OTP verification successful:', response);

      // Track signup completion
      const signupMethod = detectInputType(emailOrPhone);
      trackSignupCompleted(signupMethod, response.user?.id);

      setSuccess('Account created successfully! Redirecting...');

      // Navigate to complete profile page immediately
      navigate('/complete-profile');

    } catch (err) {
      console.error('Verify OTP error:', err);
      setError(err instanceof Error ? err.message : 'OTP verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleGoogleSignupClick = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setIsLoading(true);
        setError(null);
        
        console.log('Google OAuth success for signup, token received');
        
        // Send with 'signup' intent
        const result = await googleLogin(tokenResponse.access_token, 'signup');
        
        if (result.success && result.data) {
          console.log('✅ Google signup successful');
          console.log('Auth type:', result.data.auth_type);
          
          // Check auth type and redirect accordingly
          if (result.data.auth_type === 'registration' || 
              result.data.auth_type === 'registration_pending' || 
              result.data.requires_profile_creation) {
            // Track signup completion for Google
            trackSignupCompleted('google', result.data.user?.id);
            setSuccess('Account created successfully! Please complete your profile...');
            navigate('/complete-profile');
          } else {
            // Existing user trying to sign up - redirect to dashboard
            setSuccess('Welcome back! Redirecting to dashboard...');
            navigate('/dashboard');
          }
        } else {
          setError(result.message || 'Google signup failed. Please try again.');
        }
      } catch (err) {
        console.error('Google signup error:', err);
        setError(err instanceof Error ? err.message : 'Google signup failed');
      } finally {
        setIsLoading(false);
      }
    },
    onError: () => {
      setError('Google signup was unsuccessful. Please try again.');
    },
  });

  return (
    <PageContainer>
      <LeftPanel>
        <Box sx={{ width: '100%', maxWidth: 448, mx: 'auto' }}>
          {/* Header */}
          <Typography
            sx={{
              fontSize: { xs: '1.875rem', sm: '2.25rem' },
              fontWeight: 700,
              lineHeight: 1.2,
              color: '#111827',
              mb: 1
            }}
          >
            Create your account
          </Typography>
          <Typography sx={{ fontSize: '1rem', color: '#6B7280', mb: 4 }}>
            Already have an account?{' '}
            <MuiLink
              href="/signin"
              onClick={(e) => {
                e.preventDefault();
                navigate('/signin');
              }}
              sx={{
                color: '#4F46E5',
                fontWeight: 500,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                  color: '#4338CA'
                }
              }}
            >
              Sign in
            </MuiLink>
          </Typography>

          {/* Alerts */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Social Sign Up */}
          <Stack spacing={1.5} sx={{ mb: 4 }}>
            <SocialButton onClick={() => handleGoogleSignupClick()} disabled={isLoading}>
              <Box
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#EA4335' }}>
                  <path d="M20.283 10.356h-8.327v3.451h4.792c-.446 2.193-2.313 3.453-4.792 3.453a5.27 5.27 0 0 1-5.279-5.28 5.27 5.27 0 0 1 5.279-5.279c1.259 0 2.397.447 3.29 1.178l2.6-2.599c-1.584-1.381-3.615-2.233-5.89-2.233a8.908 8.908 0 0 0-8.934 8.934 8.907 8.907 0 0 0 8.934 8.934c4.467 0 8.529-3.249 8.529-8.934 0-.528-.081-1.097-.202-1.625z" />
                </svg>
              </Box>
              Sign up with Google
            </SocialButton>

            <SocialButton>
              <Box
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#1877F2' }}>
                  <path d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202h3.312z" />
                </svg>
              </Box>
              Sign up with Facebook
            </SocialButton>
          </Stack>

          {/* Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Box sx={{ flex: 1, height: 1, bgcolor: '#E5E7EB' }} />
            <Typography sx={{ px: 2, fontSize: '0.875rem', color: '#6B7280' }}>
              Or continue with
            </Typography>
            <Box sx={{ flex: 1, height: 1, bgcolor: '#E5E7EB' }} />
          </Box>

          {/* OTP Form */}
          <Box>
            {!otpSent ? (
              // Step 1: Enter Email/Phone and Get OTP
              <Stack spacing={2.5}>
                <Box>
                  <Typography
                    component="label"
                    sx={{
                      display: 'block',
                      fontSize: '1rem',
                      fontWeight: 500,
                      color: '#111827',
                      mb: 1.25
                    }}
                  >
                    Email or Phone
                  </Typography>
                  <StyledTextField
                    fullWidth
                    type="text"
                    placeholder="Enter email or phone (with country code, e.g., +1234567890)"
                    value={emailOrPhone}
                    onChange={(e) => setEmailOrPhone(e.target.value)}
                    required
                  />
                </Box>

                <PrimaryButton onClick={handleGetOTP} disabled={isLoading}>
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Get OTP'
                  )}
                </PrimaryButton>
              </Stack>
            ) : (
              // Step 2: Enter OTP and Verify
              <Stack spacing={3}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 500,
                      color: '#111827',
                      mb: 1.25,
                      textAlign: 'center'
                    }}
                  >
                    Enter verification code
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: '#6B7280',
                      mb: 3,
                      textAlign: 'center'
                    }}
                  >
                    We sent a 6-digit code to{' '}
                    <Box component="span" sx={{ fontWeight: 600, color: '#111827' }}>
                      {emailOrPhone}
                    </Box>
                  </Typography>

                  <OTPInput value={otp} onChange={setOtp} />
                </Box>

                {/* Resend OTP */}
                <Box sx={{ textAlign: 'center' }}>
                  {resendTimer > 0 ? (
                    <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
                      Resend code in{' '}
                      <Box component="span" sx={{ fontWeight: 600, color: '#4F46E5' }}>
                        {formatTime(resendTimer)}
                      </Box>
                    </Typography>
                  ) : (
                    <MuiLink
                      component="button"
                      type="button"
                      onClick={handleResendOTP}
                      disabled={isLoading}
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#4F46E5',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: '#4338CA'
                        },
                        '&:disabled': {
                          color: '#9CA3AF',
                          cursor: 'not-allowed'
                        }
                      }}
                    >
                      Resend OTP
                    </MuiLink>
                  )}
                </Box>

                <PrimaryButton
                  onClick={handleVerifyOTP}
                  disabled={isLoading || otp.join('').length !== 6}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Verify & Create Account'
                  )}
                </PrimaryButton>

                {/* Change Contact */}
                <Box sx={{ textAlign: 'center' }}>
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={() => {
                      setOtpSent(false);
                      setOtp(new Array(6).fill(''));
                      setResendTimer(0);
                      setError(null);
                      setSuccess(null);
                    }}
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#6B7280',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: '#111827'
                      }
                    }}
                  >
                    Change {detectInputType(emailOrPhone) === 'email' ? 'email' : 'phone number'}
                  </MuiLink>
                </Box>
              </Stack>
            )}
          </Box>

          {/* Terms */}
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: '#6B7280',
              textAlign: 'center',
              mt: 4
            }}
          >
            By creating an account, you agree to our{' '}
            <MuiLink
              href="/terms"
              sx={{
                color: '#4F46E5',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Terms of Service
            </MuiLink>{' '}
            and{' '}
            <MuiLink
              href="/privacy"
              sx={{
                color: '#4F46E5',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Privacy Policy
            </MuiLink>
          </Typography>
        </Box>
      </LeftPanel>

      <RightPanel>
        <Box sx={{ position: 'relative' }}>
          {/* Carousel Images */}
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '12px',
              mb: 4
            }}
          >
            {slides.map((slide, index) => (
              <Box
                key={index}
                sx={{
                  display: currentSlide === index ? 'block' : 'none',
                  transition: 'opacity 0.5s ease-in-out',
                  opacity: currentSlide === index ? 1 : 0
                }}
              >
                <Box
                  component="img"
                  src={slide.image}
                  alt={slide.title}
                  sx={{
                    width: '100%',
                    maxWidth: 600,
                    height: 'auto',
                    mx: 'auto',
                    display: 'block',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                  }}
                />
              </Box>
            ))}
          </Box>

          {/* Carousel Content */}
          <Box sx={{ maxWidth: 672, mx: 'auto', textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#111827',
                mb: 1.25,
                transition: 'opacity 0.3s ease-in-out',
                minHeight: '2rem'
              }}
            >
              {slides[currentSlide].title}
            </Typography>
            <Typography
              sx={{
                fontSize: '1rem',
                lineHeight: 1.6,
                color: '#6B7280',
                mb: 5,
                transition: 'opacity 0.3s ease-in-out',
                minHeight: '3rem'
              }}
            >
              {slides[currentSlide].description}
            </Typography>

            {/* Carousel Indicators */}
            <Stack direction="row" spacing={1.5} justifyContent="center" alignItems="center">
              {slides.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  sx={{
                    width: currentSlide === index ? 80 : 48,
                    height: 6,
                    borderRadius: '999px',
                    bgcolor: currentSlide === index ? '#F97316' : '#E5E7EB',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: currentSlide === index ? '#F97316' : '#D1D5DB'
                    }
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Box>
      </RightPanel>
    </PageContainer>
  );
};

export default SignUp;
